import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, Smartphone, Wifi, Radio, Settings, FileCheck, Truck, Cpu, Wrench } from 'lucide-react';

const HERO_BG = 'https://images.unsplash.com/photo-1711535767116-6bc0d7948fb9?w=1920&q=80';

export function TelecomPage() {
  const { i18n } = useTranslation();

  const products = [
    {
      number: '01',
      icon: Smartphone,
      title: i18n.language === 'fr' ? 'Feature Phones' : i18n.language === 'ar' ? 'هواتف بسيطة' : 'Feature Phones',
      desc: i18n.language === 'fr' 
        ? 'Téléphones mobiles personnalisés, du design à la production en série.'
        : i18n.language === 'ar'
        ? 'هواتف محمولة مخصصة، من التصميم إلى الإنتاج بالجملة.'
        : 'Custom mobile phones, from design to mass production.'
    },
    {
      number: '02',
      icon: Wifi,
      title: i18n.language === 'fr' ? 'Modems & Routeurs' : i18n.language === 'ar' ? 'مودم وراوتر' : 'Modems & Routers',
      desc: i18n.language === 'fr'
        ? 'Solutions de connectivité 4G/5G pour particuliers et entreprises.'
        : i18n.language === 'ar'
        ? 'حلول اتصال 4G/5G للأفراد والشركات.'
        : '4G/5G connectivity solutions for individuals and businesses.'
    },
    {
      number: '03',
      icon: Radio,
      title: i18n.language === 'fr' ? 'Terminaux IoT' : i18n.language === 'ar' ? 'أجهزة إنترنت الأشياء' : 'IoT Terminals',
      desc: i18n.language === 'fr'
        ? 'Dispositifs connectés sur mesure pour applications M2M et IoT.'
        : i18n.language === 'ar'
        ? 'أجهزة متصلة مخصصة لتطبيقات M2M وإنترنت الأشياء.'
        : 'Custom connected devices for M2M and IoT applications.'
    }
  ];

  const methodology = [
    {
      step: '01',
      title: i18n.language === 'fr' ? 'Brief & Analyse' : i18n.language === 'ar' ? 'الموجز والتحليل' : 'Brief & Analysis',
      icon: FileCheck
    },
    {
      step: '02',
      title: i18n.language === 'fr' ? 'Conception' : i18n.language === 'ar' ? 'التصميم' : 'Design',
      icon: Settings
    },
    {
      step: '03',
      title: i18n.language === 'fr' ? 'Prototypage' : i18n.language === 'ar' ? 'النموذج الأولي' : 'Prototyping',
      icon: Cpu
    },
    {
      step: '04',
      title: i18n.language === 'fr' ? 'Validation' : i18n.language === 'ar' ? 'التحقق' : 'Validation',
      icon: FileCheck
    },
    {
      step: '05',
      title: i18n.language === 'fr' ? 'Production' : i18n.language === 'ar' ? 'الإنتاج' : 'Production',
      icon: Wrench
    },
    {
      step: '06',
      title: i18n.language === 'fr' ? 'Livraison' : i18n.language === 'ar' ? 'التسليم' : 'Delivery',
      icon: Truck
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div data-testid="telecom-page">
      {/* Hero Section with background */}
      <section className="relative min-h-[70vh] flex items-center" data-testid="telecom-hero">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="Telecom" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>
        
        <div className="container-main relative z-10 py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-label text-gold mb-6">
              {i18n.language === 'fr' ? 'PRODUITS TÉLÉCOM' : i18n.language === 'ar' ? 'منتجات الاتصالات' : 'TELECOM PRODUCTS'}
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95] mb-8">
              {i18n.language === 'fr' ? (
                <>
                  Conception<br/>
                  <span className="text-gold">sur mesure</span>
                </>
              ) : i18n.language === 'ar' ? (
                <>
                  تصميم<br/>
                  <span className="text-gold">مخصص</span>
                </>
              ) : (
                <>
                  Custom<br/>
                  <span className="text-gold">design</span>
                </>
              )}
            </h1>
            <p className="text-lg text-white/70 max-w-2xl">
              {i18n.language === 'fr'
                ? 'Du cahier des charges à la livraison, nous concevons et produisons vos terminaux télécom sur mesure.'
                : i18n.language === 'ar'
                ? 'من دفتر الشروط إلى التسليم، نصمم وننتج أجهزة الاتصالات الخاصة بك حسب الطلب.'
                : 'From specifications to delivery, we design and produce your custom telecom terminals.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Section */}
      <section className="section-dark section-xl" data-testid="products-section">
        <div className="container-main">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="mb-16"
          >
            <motion.p variants={itemVariants} className="text-label text-gold mb-4">
              {i18n.language === 'fr' ? 'NOS PRODUITS' : i18n.language === 'ar' ? 'منتجاتنا' : 'OUR PRODUCTS'}
            </motion.p>
            <motion.h2 variants={itemVariants} className="text-display text-white">
              {i18n.language === 'fr' ? 'Terminaux télécom' : i18n.language === 'ar' ? 'أجهزة الاتصالات' : 'Telecom terminals'}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid md:grid-cols-3 gap-8"
          >
            {products.map((product) => (
              <motion.div 
                key={product.number} 
                variants={itemVariants}
                className="card-numbered-dark group"
              >
                <span className="card-number">{product.number}</span>
                <div className="flex items-center gap-3 mb-4">
                  <product.icon className="w-6 h-6 text-gold" />
                  <h3 className="text-heading text-white">{product.title}</h3>
                </div>
                <p className="text-muted text-body">{product.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="section-cream section-xl" data-testid="methodology-section">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-label text-gold mb-4">
              {i18n.language === 'fr' ? 'NOTRE MÉTHODOLOGIE' : i18n.language === 'ar' ? 'منهجيتنا' : 'OUR METHODOLOGY'}
            </p>
            <h2 className="text-display">
              {i18n.language === 'fr' ? (
                <>6 étapes <span className="text-gold">clés</span></>
              ) : i18n.language === 'ar' ? (
                <>6 خطوات <span className="text-gold">رئيسية</span></>
              ) : (
                <>6 key <span className="text-gold">steps</span></>
              )}
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6"
          >
            {methodology.map((step, index) => (
              <motion.div 
                key={step.step}
                variants={itemVariants}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-black flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-6 h-6 text-gold" />
                </div>
                <span className="text-2xl font-bold text-gold block mb-2">{step.step}</span>
                <span className="text-sm font-medium">{step.title}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-dark section-lg" data-testid="telecom-cta">
        <div className="container-main text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-display text-white mb-6">
              {i18n.language === 'fr' ? (
                <>Un projet <span className="text-gold">télécom</span> ?</>
              ) : i18n.language === 'ar' ? (
                <>مشروع <span className="text-gold">اتصالات</span> ؟</>
              ) : (
                <>A <span className="text-gold">telecom</span> project?</>
              )}
            </h2>
            <p className="text-white/60 text-body-lg max-w-xl mx-auto mb-10">
              {i18n.language === 'fr'
                ? 'Parlons de votre projet et trouvons ensemble la solution adaptée à vos besoins.'
                : i18n.language === 'ar'
                ? 'دعنا نتحدث عن مشروعك ونجد معًا الحل المناسب لاحتياجاتك.'
                : 'Let\'s talk about your project and find the right solution for your needs.'}
            </p>
            <Link to="/contact" className="btn-gold">
              {i18n.language === 'fr' ? 'Discuter du projet' : i18n.language === 'ar' ? 'مناقشة المشروع' : 'Discuss the project'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default TelecomPage;
